import { ColumnState } from 'core/ui';

/**
 * Utility for optimizing API calls based on visible columns
 */
// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export class ColumnOptimizationService {
  /**
   * Extract visible field names from columnsState
   */
  static getVisibleFields(columnsState: Record<string, ColumnState> | null | undefined): string[] {
    if (!columnsState) return [];

    return Object.entries(columnsState)
      .filter(([_, state]) => state.show)
      .map(([field, _]) => field);
  }

  /**
   * Determine if expensive collections should be included based on visible columns
   */
  static shouldIncludeExpensiveFields(visibleFields: string[]): {
    includeTags: boolean;
    includeServices: boolean;
    includeFiles: boolean;
    includeCalculatedFields: boolean;
  } {
    const expensiveCollections = {
      tags: ['tags'],
      services: ['services'],
      files: ['files', 'finalReportFile', 'hasFileAttachments'],
      calculated: ['slaRemaining', 'tatPercent'],
    };

    return {
      includeTags: visibleFields.some((field) => expensiveCollections.tags.includes(field)),
      includeServices: visibleFields.some((field) => expensiveCollections.services.includes(field)),
      includeFiles: visibleFields.some((field) => expensiveCollections.files.includes(field)),
      includeCalculatedFields: visibleFields.some((field) => expensiveCollections.calculated.includes(field)),
    };
  }

  /**
   * Create optimization parameters for backend
   */
  static createOptimizationParams(
    columnsState: Record<string, ColumnState> | null | undefined,
    worklistViewId?: number | null,
  ): {
    visibleFields?: string[];
    worklistViewId?: number;
    includeFiles?: boolean;
    includeTags?: boolean;
    includeServices?: boolean;
    includeCalculatedFields?: boolean;
  } {
    const visibleFields = this.getVisibleFields(columnsState);
    const expensiveFields = this.shouldIncludeExpensiveFields(visibleFields);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const params: any = {};

    // Add worklistViewId if available (primary optimization)
    if (worklistViewId) {
      params.worklistViewId = worklistViewId;
    }

    // Add visible fields for advanced optimization
    if (visibleFields.length > 0) {
      params.visibleFields = visibleFields;
    }

    // Add specific expensive field flags
    if (expensiveFields.includeFiles) {
      params.includeFiles = true;
    }
    if (expensiveFields.includeTags) {
      params.includeTags = true;
    }
    if (expensiveFields.includeServices) {
      params.includeServices = true;
    }
    if (expensiveFields.includeCalculatedFields) {
      params.includeCalculatedFields = true;
    }

    return params;
  }

  /**
   * Convert optimization params to query string
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static toQueryString(params: Record<string, any>): string {
    const queryParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          queryParams.append(key, value.join(','));
        } else {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call
          queryParams.append(key, value.toString());
        }
      }
    });

    const queryString = queryParams.toString();
    return queryString ? `&${queryString}` : '';
  }
}
